import {List, OrderedSet, Map} from 'immutable';
import i18next from 'i18next';

import {makeReducer, match} from 'web-app/util/redux';
import {type Plan} from 'web-app/react/entities/plans/model';
import {createPlan} from 'web-app/react/plans/manage-plan/actions';
import {
    fetchChildPlansDataSuccess,
    fetchChildPlansDataFailed,
} from 'web-app/react/profile/child-profile/plans-and-invoices/actions';

import * as Actions from './actions';
import {PlanError, ManagePlansState, type PlanPreviewStates, type PlanPreviewState} from './types';

const getChildHasPlanPreviewsLeft = (childPreviews: PlanPreviewStates) => {
    return childPreviews.some(preview => !preview.hasSaved);
};

const updateChildPlanPreviews = (
    childPlanPreviews: PlanPreviewStates,
    plan: Plan,
    updateFunc: (previewState: PlanPreviewState) => PlanPreviewState,
) => {
    const planPreviewIndex = childPlanPreviews
        ? childPlanPreviews.findIndex(previewState => {
              return Boolean(previewState.plan && previewState.plan.id === plan.id);
          })
        : -1;

    return planPreviewIndex > -1 && childPlanPreviews
        ? childPlanPreviews.update(planPreviewIndex, updateFunc)
        : childPlanPreviews;
};

const managePlans = makeReducer<ManagePlansState>(
    [
        match(Actions.sessionsAndProductsFetched, state =>
            state.merge({
                sessionsAndProductsFetched: true,
            }),
        ),
        match(Actions.toggleScheduleChange, (state, {payload}) =>
            state.merge({
                currentStep: 0,
                showScheduleChange: payload.show,
            }),
        ),
        match(Actions.stepChanged, (state, {payload}) =>
            state.merge({
                currentStep: payload.step,
            }),
        ),
        match(Actions.changeHighlightBy, (state, {payload}) =>
            state.merge({
                selectedChildren: OrderedSet(),
                highlightBy: payload.highlightBy,
            }),
        ),
        match(Actions.changeChildrenToShow, (state, {payload}) =>
            state.merge({
                selectedChildren: OrderedSet(),
                childrenToShow: payload.childrenToShow,
            }),
        ),

        match(Actions.changeGroup, (state, {payload}) =>
            state.merge({
                selectedChildren: OrderedSet(),
                selectedGroup: payload.groupId,
            }),
        ),

        match(Actions.showing, (state, {payload}) =>
            state.merge({
                showing: payload.showing,
            }),
        ),

        match(Actions.selectChild, (state, {payload}) =>
            state.merge({
                selectedChildren: payload.select
                    ? state.selectedChildren.add(payload.childId)
                    : state.selectedChildren.delete(payload.childId),
            }),
        ),

        match(Actions.selectChildForChange, (state, {payload}) =>
            state.merge({
                selectedChildrenForChange: payload.select
                    ? state.selectedChildrenForChange.add(payload.childId)
                    : state.selectedChildrenForChange.delete(payload.childId),
            }),
        ),

        match(Actions.selectAllChildren, (state, {payload}) =>
            state.merge({
                selectedChildren: payload.childIds || OrderedSet(),
            }),
        ),

        match(Actions.fetchPlans, state =>
            state.merge({
                updatingChildren: true,
            }),
        ),

        match(Actions.fetchPlansSuccess, (state, {payload}) =>
            state.merge({
                updatingChildren: false,
                allPlansFetched: Boolean(payload.allPlansFetched),
            }),
        ),

        match(Actions.fetchPlansFailed, state =>
            state.merge({
                updatingChildren: false,
            }),
        ),

        match(
            Actions.fetchAll,
            () =>
                new ManagePlansState({
                    childIds: List(),
                    selectedChildren: OrderedSet(),
                    showing: true,
                    updatingChildren: true,
                }),
        ),

        match(Actions.fetchChildrenAndPlans, state =>
            state.merge({
                selectedChildren: OrderedSet(),
                updatingChildren: true,
            }),
        ),

        match(Actions.doPlanPreview, (state, {payload}) =>
            state.merge({
                sessionsAndProductsFetched: false,
                planPreviews: Map(),
                selectedChildrenForChange: OrderedSet(),
                previewData: payload.formData,
                numberOfChildrenScheduled: 0,
                numberOfChildrenToSave: 0,
            }),
        ),
        match(Actions.doPlanPreviewSuccess, (state, {payload}) => {
            const {childPreviewStates} = payload;
            return state.merge({
                selectedChildrenForChange: state.selectedChildrenForChange.add(payload.childId),
                planPreviews: state.planPreviews.set(payload.childId, childPreviewStates),
            });
        }),
        match(Actions.doPlanPreviewFailed, (state, {payload}) =>
            state.merge({
                planPreviews: state.planPreviews.set(
                    payload.childId,
                    List.of({
                        error: new PlanError({
                            error: payload?.e?.error ?? i18next.t('somethingWentWrong'),
                        }),
                    }),
                ),
            }),
        ),
        match(Actions.doPlanPreviewAborted, (state, {payload}) =>
            state.merge({
                planPreviews: state.planPreviews.set(payload.childId, List()),
            }),
        ),
        match(Actions.schedulePlanChangeForSelectedChildren, state =>
            state.merge({
                numberOfChildrenToSave: state.selectedChildrenForChange.size,
                numberOfChildrenScheduled: 0,
            }),
        ),
        match(Actions.schedulePlanChangeForChildSuccess, (state, {payload}) => {
            // If a child has more that 1 plan, assume that we aren't done updating plans for that child and remove that plan from the list
            // otherwise just remove the child completely from bulk change
            const childPlanPreviews = state.planPreviews.get(payload.childId);

            const newChildPreviews = childPlanPreviews
                ? updateChildPlanPreviews(childPlanPreviews, payload.previewPlan, previewState => ({
                      ...previewState,
                      hasSaved: true,
                  }))
                : childPlanPreviews;

            const newPlanPreviews =
                newChildPreviews && newChildPreviews.size > 1
                    ? state.planPreviews.set(payload.childId, newChildPreviews)
                    : state.planPreviews;

            const childHasPlanPreviewsLeft = newChildPreviews && getChildHasPlanPreviewsLeft(newChildPreviews);
            return state.merge({
                numberOfChildrenScheduled: childHasPlanPreviewsLeft
                    ? state.numberOfChildrenScheduled
                    : state.numberOfChildrenScheduled + 1,
                planPreviews: newPlanPreviews,
            });
        }),
        match(Actions.resetSelectedChildren, state => {
            return state.merge({
                numberOfChildrenScheduled: 0,
            });
        }),
        match(Actions.schedulePlanChangeForChildFailed, (state, {payload}) => {
            const childPlanPreviews = state.planPreviews.get(payload.childId);

            const newChildPreviews = childPlanPreviews
                ? updateChildPlanPreviews(childPlanPreviews, payload.previewPlan, previewState => ({
                      ...previewState,
                      error: new PlanError({
                          error: payload?.e?.error ?? i18next.t('somethingWentWrong'),
                      }),
                  }))
                : childPlanPreviews;

            const newPlanPreviews =
                newChildPreviews && newChildPreviews.size > 1
                    ? state.planPreviews.set(payload.childId, newChildPreviews)
                    : state.planPreviews;
            const childHasPlanPreviewsLeft = newChildPreviews && getChildHasPlanPreviewsLeft(newChildPreviews);

            return state.merge({
                numberOfChildrenScheduled: childHasPlanPreviewsLeft
                    ? state.numberOfChildrenScheduled
                    : state.numberOfChildrenScheduled + 1,
                planPreviews: newPlanPreviews,
            });
        }),
        match(Actions.deletePlansForSelectedChildren, state =>
            state.merge({
                numberOfChildrenToSave: state.selectedChildrenForChange.size,
                numberOfChildrenScheduled: 0,
            }),
        ),
        match(Actions.deletePlansForSelectedChildrenSuccess, (state, {payload}) => {
            if (!payload.plan.childId) {
                return state;
            }

            return state.merge({
                numberOfChildrenScheduled: state.numberOfChildrenScheduled + 1,
                planPreviews: state.planPreviews.remove(payload.plan.childId),
            });
        }),
        match(Actions.deletePlansForSelectedChildrenFailed, (state, {payload}) => {
            if (!payload.plan.childId) {
                return state;
            }

            const childPlanPreviews = state.planPreviews.get(payload.plan.childId);

            const newChildPreviews = childPlanPreviews
                ? updateChildPlanPreviews(childPlanPreviews, payload.plan, previewState => ({
                      ...previewState,
                      error: new PlanError({
                          error: payload?.e?.error ?? i18next.t('somethingWentWrong'),
                      }),
                  }))
                : undefined;

            const newPlanPreviews =
                newChildPreviews && newChildPreviews.size > 1
                    ? state.planPreviews.set(payload.plan.childId, newChildPreviews)
                    : state.planPreviews;
            const childHasPlanPreviewsLeft = newChildPreviews && getChildHasPlanPreviewsLeft(newChildPreviews);

            return state.merge({
                numberOfChildrenScheduled: childHasPlanPreviewsLeft
                    ? state.numberOfChildrenScheduled
                    : state.numberOfChildrenScheduled + 1,
                planPreviews: newPlanPreviews,
            });
        }),
        match(Actions.fixPlan, (state, {payload}) => {
            const {planPreviews} = state;

            if (!payload.plan.childId || !planPreviews.has(payload.plan.childId)) {
                return state;
            }

            const newPlanPreviews = planPreviews.update(payload.plan.childId, previewStates => {
                return previewStates.map(previewState => {
                    if (previewState.plan && previewState.plan.id === payload.plan.id) {
                        return {
                            plan: payload.plan,
                            isUpdate: previewState.isUpdate,
                        };
                    }
                    return previewState;
                });
            });

            return state.merge({
                planPreviews: newPlanPreviews,
            });
        }),
        match(Actions.fetchChildrenAndPlansSuccess, (state, {payload}) => {
            return state.merge({
                updatingChildren: false,
                allPlansFetched: payload.allPlansFetched,
                selectedChildren: OrderedSet(),
                childIds: payload.children.map(child => child.childId),
            });
        }),
        match(Actions.fetchAllFailed, state =>
            state.merge({
                updatingChildren: false,
            }),
        ),
        match(Actions.fetchChildrenAndPlansFailed, state =>
            state.merge({
                updatingChildren: false,
            }),
        ),
        match(Actions.sortChildren, (state, {payload}) => state.merge({sorting: payload.sorting})),
        match(createPlan, (state, {payload}) =>
            state.merge({childLoading: state.childLoading.set(payload.plan.childId, true)}),
        ),
        match(fetchChildPlansDataSuccess, (state, {payload}) =>
            state.merge({childLoading: state.childLoading.set(payload.childId, false)}),
        ),
        match(fetchChildPlansDataFailed, (state, {payload}) =>
            state.merge({childLoading: state.childLoading.set(payload.childId, false)}),
        ),
    ],
    new ManagePlansState(),
);

export default managePlans;
